import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, Ref } from 'vue';
import { makeFormModel, setApiErrorMessages, setState, toDataModel, updateModel } from 'ah-common-lib/src/form/helpers';
import { radioField, textField, emailField, phoneField, financialAmountField } from 'ah-common-lib/src/form/models';
import {
  BaseIndividual,
  ClientFileCategories,
  ClientFileCategoriesToHuman,
  ClientFileCategoryDescriptions,
  EntityAddressHistory,
  UnsubmittedUbo,
  UploadedFile,
  VersionedObject,
} from 'ah-api-gateways';
import { generateTempUUID } from 'ah-common-lib/src/helpers/uuid';
import { FormModel, FormEvent, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { cloneDeep, debounce, isEqual } from 'lodash';
import { requiredIfStateValue, optional, checkParam, validName } from 'ah-common-lib/src/form/validators';
import FileUploader from 'ah-common-lib/src/common/components/upload/FileUploader.vue';
import { personTitleField } from 'ah-common-lib/src/form/formModels';
import { defaultAcceptanceFileTypes } from 'ah-common-lib/src/helpers/file';
import { map, tap } from 'rxjs/operators';
import { useAuthStore } from '@/app/store/authStore';
import { useIndividualSettingsStore } from '@/app/store/individualSettingsModule';
import { waitForEntityChange } from 'ah-requests';
import { registrationDateField } from '@/app/helpers/registration/forms';
import { useFEFeatureFlag } from 'ah-common-stores';
import RegistrationAddressForm from '../common/RegistrationAddressForm.vue';
import OnboardingPreviousAddressesForms from '../common/OnboardingPreviousAddressesForms.vue';
import RemoveButton from '../common/RemoveButton.vue';
import { required } from '@vuelidate/validators';
import { getServices } from '@/app/services';
import { constructPayloadErrors } from 'ah-requests/helpers/apiErrors';

/**
 * Ultimate beneficiary owner(s) form
 *
 * The current component will display a form to add (if any) `maxUBO` number
 * of UBO users.
 *
 * Emits:
 * - update:ubos (payload: UnsubmittedUbo[])
 * - update:files (payload: UploadedFile[])
 */

interface UboFormDef {
  ubo: UnsubmittedUbo;
  form: FormModel;
  validation: FormValidation | null;
  currentAddressValidation: FormValidation | null;
  previousAddressesValidation: FormValidation | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'UBOForm',
  props: {
  ubos: { type: Array as () => UnsubmittedUbo[], default: () => [] },
  files: { type: Array as () => UploadedFile[], default: () => [] },
  showUboAddressHistoryErrors: { type: Boolean, default: false },
},
  emits: {
  'update:ubos': (_value: UnsubmittedUbo[]) => true,
  'update:files': (_value: UploadedFile[]) => true,
  'update:validation': (_value: FormValidation) => true,
},
  setup(__props, { emit }) {

const props = __props;





const uboUserFM = (useDOB = false) =>
  makeFormModel({
    name: 'uboUserForm',
    fieldType: 'form',
    fields: [
      personTitleField({ fieldWrapperClass: 'col col-2', showRequiredMarkers: true }),
      textField(
        'firstName',
        'First Name',
        { fieldWrapperClass: 'col col-5', showRequiredMarkers: true },
        { required: requiredIfStateValue('firstName'), validName }
      ),
      textField(
        'lastName',
        'Last Name',
        { fieldWrapperClass: 'col col-5', showRequiredMarkers: true },
        { required: requiredIfStateValue('firstName'), validName }
      ),
      financialAmountField(
        'ownershipPercentage',
        'Percentage of Ownership',
        {
          fieldWrapperClass: 'col col-5',
          label: 'You may include up to 2 decimal places.',
          errorMessages: {
            ownershipLimits: 'Must be a value between 25% and 100%',
          },
        },
        {
          required,
          ownershipLimits: optional((value) => value >= 25 && value <= 100),
        }
      ),
      emailField('email', 'Email Address', { showRequiredMarkers: true }),
      phoneField(
        'phoneNumber',
        'Mobile Number',
        { fieldWrapperClass: 'col col-6' },
        {
          phone: optional(checkParam('phoneNumber', 'valid')),
        }
      ),
      ...(useDOB ? [registrationDateField({ fieldWrapperClass: 'col col-6', showRequiredMarkers: true })] : []),
    ],
  });

const uboChoiceFM = () =>
  makeFormModel({
    name: 'uboChoiceForm',
    fieldType: 'form',
    fields: [
      radioField(
        'choice',
        'Are there any UBOs?',
        [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
        {
          defaultValue: 'no',
          inline: true,
          fieldWrapperClass: 'mb-0 col col-12',
          showRequiredMarkers: true,
        }
      ),
    ],
  });

const dobInOnboardingFeatureActive = useFEFeatureFlag('dobInOnboardingFeatureActive');
const hideOnboardingDocumentsUploader = useFEFeatureFlag('hideOnboardingDocumentsUploader');

const authStore = useAuthStore();
const individualSettingsStore = useIndividualSettingsStore();
const services = getServices();

const uboChoiceForm = ref(uboChoiceFM());
const uboChoiceValidation = ref<FormValidation | null>(null);
const uboUserFormDefs: Ref<UboFormDef[]> = ref<UboFormDef[]>([]);
const innerFiles = ref<UploadedFile[]>([]);

const MAX_ALLOWED_UBOS = 4;

const resolutionLetterTitle = computed(() => ClientFileCategoriesToHuman[ClientFileCategories.RESOLUTION_LETTER]);
const resolutionLetterDescription = computed(
  () => ClientFileCategoryDescriptions[ClientFileCategories.RESOLUTION_LETTER]
);

const isUboAUKResident = (ubo?: UnsubmittedUbo) => {
  return ubo?.currentAddress && ubo.currentAddress.countryCode === 'GB';
};

const resolutionLetter = computed(() => {
  return innerFiles.value.find((document) => document.category === ClientFileCategories.RESOLUTION_LETTER) || null;
});

const shouldDisplayUbos = computed(() => {
  return uboChoiceForm.value.choice === 'yes';
});

const uboAddressApiErrors = computed(() =>
  props.ubos.map((ubo) =>
    ubo.addressHistoryApiError ? constructPayloadErrors<EntityAddressHistory>(ubo.addressHistoryApiError) : undefined
  )
);

const validUploadedFiles = computed(() => {
  return true;
  // FIXME: documents are no longer required in UBOs (temporary fix)

  // if (!this.shouldDisplayUbos) {
  //   return true;
  // } else {
  //   const anyIncompleteUboFiles = this.uboUsers.find((user) =>
  //     [ClientFileCategories.PROOF_OF_ADDRESS, ClientFileCategories.PHOTO_ID].find(
  //       (cat) =>
  //         !user.documents.find((d) => d.category === cat) &&
  //         !user.readyToUpload?.find((d) => d.category === cat && d.file)
  //     )
  //   );

  //   return !anyIncompleteUboFiles;
  // }
});

const validation = computed<FormValidation>(() => {
  let areAnyUbosInvalid = false;
  let areAnyUbosDirty = false;

  if (uboChoiceForm.value.choice === 'yes') {
    areAnyUbosInvalid = uboUserFormDefs.value?.some(
      (formDef) =>
        formDef.currentAddressValidation?.$invalid ||
        (isUboAUKResident(formDef.ubo) && formDef.previousAddressesValidation?.$invalid) ||
        formDef.validation?.$invalid
    );
    areAnyUbosDirty = uboUserFormDefs.value?.some(
      (formDef) =>
        formDef.currentAddressValidation?.$dirty ||
        (isUboAUKResident(formDef.ubo) && formDef.previousAddressesValidation?.$dirty) ||
        formDef.validation?.$dirty
    );
  }

  return {
    $invalid: uboChoiceValidation.value?.$invalid || !validUploadedFiles.value || areAnyUbosInvalid,
    $dirty: !!uboChoiceValidation.value?.$dirty || areAnyUbosDirty,
    $errors: [],
    $touch: () => {},
    $reset: () => {},
    $validate: async () => {},
  } as unknown as FormValidation;
});

const maxAllowedUsersReached = computed(() => {
  return uboUserFormDefs.value.length >= MAX_ALLOWED_UBOS;
});

function makeEmptyUbo(): UnsubmittedUbo {
  return {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    birthDate: '',
    ownershipPercentage: 25,
    email: '',
    id: generateTempUUID(),
    documents: [],
    currentAddress: {
      addressLine: '',
      city: '',
      postalCode: '',
      stateOrProvince: '',
      countryCode: '',
      residingFrom: '',
    },
    previousAddresses: [],
    readyToUpload: [
      { category: ClientFileCategories.PROOF_OF_ADDRESS, file: null },
      { category: ClientFileCategories.PHOTO_ID, file: null },
    ],
  };
}

function onUpdateUbo(uboFormDef: UboFormDef, update: Partial<UnsubmittedUbo>) {
  Object.assign(uboFormDef.ubo, update);
  emitUbosUpdate();
}

// Update to ubos models is done on a debounced cycle to avoid expensive template recalculations
const debouncedUbosUpdate = debounce((ubos: UnsubmittedUbo[]) => {
  emit('update:ubos', ubos);
}, 250);

function emitUbosUpdate() {
  if (shouldDisplayUbos.value) {
    debouncedUbosUpdate(uboUserFormDefs.value.map((item) => item.ubo));
  } else {
    debouncedUbosUpdate([]);
  }
}

function uploadResolutionLetter(file: File) {
  return services.client
    .uploadDocument(authStore.loggedInIdentity!.client!.id, ClientFileCategories.RESOLUTION_LETTER, file)
    .pipe(
      tap((update) => {
        if (update.finished) {
          waitForDocument(update.file).subscribe((document) => {
            if (document) {
              individualSettingsStore.setClientDocument({ document });
              const currFileIndex = innerFiles.value.findIndex((i) => i.category === document.category);

              currFileIndex > -1
                ? innerFiles.value.splice(currFileIndex, 1, document)
                : innerFiles.value.push(document);
            }
          });
        }
      })
    );
}

function waitForDocument(file: VersionedObject) {
  return waitForEntityChange(
    () => services.client.getDocuments(authStore.loggedInIdentity!.client!.id),
    (docs) => {
      return !!docs.find((d) => d.id === file.id);
    }
  ).pipe(map((docs) => docs.find((d) => d.id === file.id)!));
}

function deleteFile(file: UploadedFile) {
  return services.client.deleteDocument(authStore.loggedInIdentity!.client!.id, file.id).pipe(
    tap(() => {
      const currFileIndex = innerFiles.value.findIndex((i) => i.category === file.category);
      if (currFileIndex > -1) {
        innerFiles.value.splice(currFileIndex, 1);
      }

      individualSettingsStore.unsetClientDocument({ category: file.category as ClientFileCategories });
    })
  );
}

function downloadFile(file: UploadedFile) {
  return services.client.downloadSyncDocument(authStore.loggedInIdentity!.client!.id, file);
}

function getFile(user: UnsubmittedUbo, category: ClientFileCategories) {
  return (
    user.readyToUpload?.find((file) => file.category === category)?.file ||
    user.documents?.find((file) => file.category === category)
  );
}

function onFileChange(file: File, user: UnsubmittedUbo, category: ClientFileCategories) {
  if (file === null) {
    onFileRemoved(file, user, category);
  }
  user.readyToUpload = user.readyToUpload || [];
  let userFile = user.readyToUpload!.find((file) => file.category === category)!;
  if (!userFile) {
    userFile = { category, file: file };
    user.readyToUpload!.push(userFile);
  } else {
    userFile.file = file;
  }
  emitUbosUpdate();
}

function onFileRemoved(file: File, user: UnsubmittedUbo, category: ClientFileCategories) {
  user.readyToUpload = user.readyToUpload || [];

  let uploaded = user.documents!.find((file) => file.category === category)!;
  let userFile = user.readyToUpload!.find((file) => file.category === category)!;
  if (uploaded) {
    user.documents.splice(user.documents.indexOf(uploaded), 1);
  }

  if (!userFile) {
    userFile = { category, file: null };
    user.readyToUpload!.push(userFile);
  } else {
    userFile.file = null;
  }

  emitUbosUpdate();
}

function onFormEvent(event: FormEvent<any>) {
  if (event.event === 'form-field-set-value') {
    uboUserFormDefs.value.forEach((uboFormDef) => {
      uboFormDef.ubo = {
        ...uboFormDef.ubo,
        ...toDataModel(uboFormDef.form),
        documents: uboFormDef.ubo.documents,
        readyToUpload: uboFormDef.ubo.readyToUpload,
      };
    });
    setState(event.model, 'errors', []);
    emitUbosUpdate();
  }
}

function onChoiceChange(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    if (shouldDisplayUbos.value && uboUserFormDefs.value.length === 0) {
      addUser();
    }
    emitUbosUpdate();
  }
}

function remove(index: number) {
  if (uboUserFormDefs.value.length > 1) {
    uboUserFormDefs.value.splice(index, 1);
    emitUbosUpdate();
  } else {
    uboUserFormDefs.value = [];
    emit('update:ubos', []);
    uboChoiceForm.value.choice = 'no';
  }
}

function addUser() {
  uboUserFormDefs.value.push(generateUboForm());
  emitUbosUpdate();
}

function generateUboForm(ubo?: UnsubmittedUbo): UboFormDef {
  return {
    form: uboUserFM(dobInOnboardingFeatureActive.value),
    currentAddressValidation: null,
    previousAddressesValidation: null,
    validation: null,
    ubo: {
      id: generateTempUUID(),
      ...(ubo ? cloneDeep(ubo) : makeEmptyUbo()),
    },
  };
}

watch(
  () => props.files,
  () => {
    if (props.files !== null) {
      innerFiles.value = props.files;
    }
  },
  { immediate: true }
);

watch(
  [
    () => props.ubos,
    dobInOnboardingFeatureActive,
    () => props.ubos.map((ubo) => ubo.userCreationApiError?.occurrenceId),
  ],
  (_newVals, oldVals) => {
    if (props.ubos && props.ubos.length > 0) {
      uboChoiceForm.value.choice = 'yes';
      uboUserFormDefs.value = props.ubos.map((ubo) => {
        const uboForm =
          uboUserFormDefs.value.find((formDef) => formDef.ubo.id && formDef.ubo.id === ubo.id) || generateUboForm(ubo);
        updateModel(uboForm.form, ubo, undefined, true);

        const isErrorUnchanged = !!(oldVals[2] || []).find(
          (oldUboError) => oldUboError === ubo.userCreationApiError?.occurrenceId
        );
        if (!isErrorUnchanged) {
          setState(uboForm.form, 'errors', [], true);
          if (ubo.userCreationApiError) {
            const payloadErrorData = constructPayloadErrors<BaseIndividual>(ubo.userCreationApiError);
            setApiErrorMessages(payloadErrorData, uboForm.form);
          }
        }

        return uboForm;
      });
    } else {
      uboChoiceForm.value.choice = 'no';
      uboUserFormDefs.value = [];
    }
  },
  { immediate: true }
);

watch(innerFiles, () => {
  if (!isEqual(innerFiles.value, props.files)) {
    emit('update:files', innerFiles.value);
  }
});

watch(
  validation,
  () => {
    if (validation.value) {
      emit('update:validation', validation.value as FormValidation);
    }
  },
  { immediate: true }
);

return { __sfc: true,props, emit, uboUserFM, uboChoiceFM, dobInOnboardingFeatureActive, hideOnboardingDocumentsUploader, authStore, individualSettingsStore, services, uboChoiceForm, uboChoiceValidation, uboUserFormDefs, innerFiles, MAX_ALLOWED_UBOS, resolutionLetterTitle, resolutionLetterDescription, isUboAUKResident, resolutionLetter, shouldDisplayUbos, uboAddressApiErrors, validUploadedFiles, validation, maxAllowedUsersReached, makeEmptyUbo, onUpdateUbo, debouncedUbosUpdate, emitUbosUpdate, uploadResolutionLetter, waitForDocument, deleteFile, downloadFile, getFile, onFileChange, onFileRemoved, onFormEvent, onChoiceChange, remove, addUser, generateUboForm, ClientFileCategories, FileUploader, defaultAcceptanceFileTypes, RegistrationAddressForm, OnboardingPreviousAddressesForms, RemoveButton }
}

})